'use client';

import { Button, Heading, Text } from '@youversion/design-system-components';
import { LockStroked } from '@youversion/design-system-icons';
import { signOut } from '@youversion/next-auth/client';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import NextAuthConfiguration from '@/auth-config';
import { useCurrentOrganizationId } from '@/context/organization/useCurrentOrganizationId';
import { useOrganization } from '@/context/organization/useOrganization';
import { useQueryParams } from '@/hooks/use-query-params';
import { fields, useSupportUrls } from '@/hooks/use-support-urls/use-support-urls';
import { errorToast } from '@/utils/toast';

interface Props {
  children: React.ReactNode;
}

export function OrganizationAccessGuard({ children }: Props) {
  const { organization_id: organizationIdFromParam } = useQueryParams();
  const { isPermissionError, isLoading, organizationId: organizationIdFromContext } = useOrganization();
  const { nukeFromOrbit } = useCurrentOrganizationId();
  const router = useRouter();
  const { t } = useTranslation(['common']);

  // The URL param can carry an org id the context hasn't caught up to yet
  // (e.g. landing straight on a deep link that 403s before context resolves).
  const effectiveOrgId = organizationIdFromParam ?? organizationIdFromContext;

  const { supportUrl: baseSupportUrl } = useSupportUrls();
  const supportUrl = React.useMemo(() => {
    const url = new URL(baseSupportUrl);
    if (effectiveOrgId) {
      // .set(), not .append(): useSupportUrls() may have already added this field from context.
      url.searchParams.set(fields.supportForm.organizationId, effectiveOrgId);
    }
    return url.href;
  }, [baseSupportUrl, effectiveOrgId]);

  const handleLogOut = React.useCallback(async () => {
    nukeFromOrbit();
    try {
      await signOut(NextAuthConfiguration);
    } catch {
      errorToast(t('common:something_went_wrong_retry'));
    }
  }, [nukeFromOrbit, t]);

  const handleGoToOrganizations = React.useCallback(() => {
    nukeFromOrbit();
    router.push('/organization-select');
  }, [nukeFromOrbit, router]);

  if (!effectiveOrgId || isLoading || !isPermissionError) {
    return <>{children}</>;
  }

  return (
    <div className='flex w-full grow items-center justify-center min-h-screen p-4'>
      <div className='flex flex-col items-center gap-4 max-w-md text-center'>
        <LockStroked className='size-16' />
        <Heading level={2} size='md' variant='headline'>
          {t('common:organization_access_denied_title')}
        </Heading>
        <Text>
          <Trans
            components={{ a: <a href={supportUrl} rel='noreferrer' target='_blank' /> }}
            i18nKey='common:organization_access_denied_description'
            t={t}
          />
        </Text>
        <div className='flex flex-col gap-3 md:flex-row'>
          <Button onClick={handleLogOut} type='button' variant='primary'>
            {t('common:log_out_and_try_again')}
          </Button>
          <Button onClick={handleGoToOrganizations} type='button' variant='borderless'>
            {t('common:my_organizations')}
          </Button>
        </div>
      </div>
    </div>
  );
}
